// PricingHistoryTimeline · Lot M3
// Timeline verticale de l'historique pricing (asking + net + origines).

import { ArrowRight, User, Tag } from "lucide-react";
import { fmtMoney } from "@/lib/money";

const KIND_META = {
  asking_price: { label: "Prix affiché", tone: "bg-[#C8FF2F]" },
  net_seller:   { label: "Net vendeur", tone: "bg-sky-400" },
};

const ORIGIN_LABELS = {
  initialization:     "Initialisation",
  agency_manual:      "Modification agence",
  proposal_accepted:  "Proposition acceptée (M7)",
};

const fmt = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
};

export default function PricingHistoryTimeline({ events = [], loading = false }) {
  if (loading) {
    return (
      <div className="space-y-3" data-testid="pricing-history-loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-2xl bg-black/[0.03]" />
        ))}
      </div>
    );
  }
  if (!events.length) {
    return (
      <div className="rounded-2xl bg-black/[0.02] px-4 py-6 text-center text-[12.5px] text-black/45"
           data-testid="pricing-history-empty">
        Aucun événement de pricing pour l&apos;instant.
      </div>
    );
  }

  return (
    <ol className="space-y-0 relative" data-testid="pricing-history-timeline">
      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-black/[0.08]" />

      {events.map((e, idx) => {
        const km = KIND_META[e.kind] || { label: e.kind, tone: "bg-black" };
        const cur = e.currency || "EUR";
        return (
          <li
            key={e.id || idx}
            className="relative pl-8 pb-6 last:pb-0"
            data-testid={`pricing-history-event-${idx}`}
          >
            <span className={`absolute left-0 top-[7px] h-[15px] w-[15px] rounded-full border-[3px] border-[#FAFAF7] ${km.tone}`} />
            <div className="flex items-center gap-2">
              <span className="text-[13.5px] font-medium text-black">
                {km.label}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-black/[0.04] px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-black/55">
                <Tag size={9} /> {ORIGIN_LABELS[e.origin] || e.origin}
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 text-[13px] font-mono">
              {e.from_value != null && (
                <>
                  <span className="text-black/50">
                    {fmtMoney(e.from_value, cur)}
                  </span>
                  <ArrowRight size={12} className="text-black/40" />
                </>
              )}
              <span className="text-black font-medium">
                {fmtMoney(e.to_value, cur)}
              </span>
            </div>
            {e.reason && (
              <div className="mt-1.5 rounded-xl bg-black/[0.03] px-3 py-1.5 text-[12px] text-black/70">
                {e.reason}
              </div>
            )}
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-black/40">
              <User size={11} strokeWidth={1.7} />
              <span>{e.actor_name || "—"}</span>
              <span>·</span>
              <time>{fmt(e.at)}</time>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
